import { Injectable, signal } from '@angular/core';
import { enum as zEnum, object, string } from 'zod';

/**
 * Shared dashboard filter state — the bridge the copilot's `set_dashboard_filters`
 * client tool writes into and the dashboard component reads from.
 *
 * The dashboard's own filter surface (chart timeframe, order-book symbol) lives as
 * component-local signals. Rather than reach into the component, the copilot tool
 * pushes validated filter intents here; the dashboard subscribes (an effect) and
 * applies the recognized ones to its local signals. This keeps the copilot tool
 * decoupled from the (large) dashboard component while remaining a REAL wiring —
 * not a write into a void.
 *
 * Only vetted keys are surfaced as typed signals (`timeframe`, `symbol`); the full
 * validated bag is also exposed via {@link filters} for future consumers. Unknown
 * or malformed inputs are ignored (fail-safe), never thrown.
 */

/** The chart timeframes the dashboard understands. */
export type DashboardTimeframe = '1H' | '24H' | '7D';

const TimeframeSchema = zEnum(['1H', '24H', '7D']);
// A permissive symbol guard: non-empty, bounded, no control/markup characters.
const SymbolSchema = string()
  .trim()
  .min(1)
  .max(32)
  .regex(/^[A-Za-z0-9._/:-]+$/);

const FiltersSchema = object({
  timeframe: TimeframeSchema.optional().catch(undefined),
  symbol: SymbolSchema.optional().catch(undefined),
});

@Injectable({ providedIn: 'root' })
export class DashboardFilterStore {
  private readonly timeframeSig = signal<DashboardTimeframe | null>(null);
  private readonly symbolSig = signal<string | null>(null);
  private readonly filtersSig = signal<Readonly<Record<string, unknown>>>({});

  /** Latest requested chart timeframe (null until a filter sets one). */
  readonly timeframe = this.timeframeSig.asReadonly();
  /** Latest requested order-book symbol (null until a filter sets one). */
  readonly symbol = this.symbolSig.asReadonly();
  /** The last full validated filter bag applied (for future consumers). */
  readonly filters = this.filtersSig.asReadonly();

  /**
   * Apply an agent-supplied filter bag. Recognized, well-formed keys update the
   * typed signals; anything unknown/malformed is dropped. Returns the set of keys
   * that were actually applied (useful for diagnostics / tests).
   */
  applyFilters(raw: unknown): readonly string[] {
    const input = raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
    const parsed = FiltersSchema.safeParse(input);
    const data = parsed.success ? parsed.data : {};

    const applied: string[] = [];
    if (data.timeframe !== undefined) {
      this.timeframeSig.set(data.timeframe);
      applied.push('timeframe');
    }
    if (data.symbol !== undefined) {
      this.symbolSig.set(data.symbol);
      applied.push('symbol');
    }
    if (applied.length > 0) {
      // Preserve the full validated bag (only the applied keys) for other readers.
      const bag: Record<string, unknown> = {};
      for (const key of applied) {
        bag[key] = (data as Record<string, unknown>)[key];
      }
      this.filtersSig.set(bag);
    }
    return applied;
  }
}
